import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DatePicker, Form, Input, Modal, Select } from 'antd';
import { Dayjs } from 'dayjs';

import { createBlueprint, selectors, REPORTING_CURRENCIES, HISTORICAL_SCENARIO, REAL_TIME_SCENARIO } from 'state';

type Props = {
  visible: boolean;
  hide: () => void;
};

// Backend expects ISO strings it can parse with datetime.fromisoformat(...rstrip('Z')).
const DATE_FMT = 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]';

/** Historical templates save a window; real-time templates save family intent. */
const CreateBlueprintModal: React.FC<Props> = ({ visible, hide }) => {
  const dispatch = useDispatch();
  const waiting = useSelector(selectors.blueprintsWaiting);
  const [form] = Form.useForm();
  const mode = Form.useWatch('scenario_type', form) ?? HISTORICAL_SCENARIO;
  // Native ownership is chosen once at creation; legacy remains the default.
  const engine = Form.useWatch('execution_engine', form) ?? 'legacy';

  return (
    <Modal
      title="Create a new blueprint"
      open={visible}
      onCancel={hide}
      footer={null}
      width={480}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          name: '',
          category: '',
          description: '',
          reporting_currency: 'USD',
          scenario_type: HISTORICAL_SCENARIO,
          dates: undefined,
          execution_engine: 'legacy',
          fast_advancement_mode: 'daily',
        }}
        onFinish={() => {
          const data = form.getFieldsValue() as {
            name: string;
            category?: string;
            description?: string;
            reporting_currency?: string;
            scenario_type: number;
            dates?: [Dayjs, Dayjs];
            execution_engine?: 'legacy' | 'fast';
            fast_advancement_mode?: 'daily' | 'news';
          };
          const [start, end] = data.dates || [];
          dispatch(
            createBlueprint({
              name: data.name,
              execution_engine: mode === HISTORICAL_SCENARIO ? data.execution_engine : 'legacy',
              fast_advancement_mode: mode === HISTORICAL_SCENARIO && engine === 'fast' ? data.fast_advancement_mode : undefined,
              category: data.category || undefined,
              description: data.description || undefined,
              reporting_currency: data.reporting_currency || undefined,
              scenario_type: data.scenario_type,
              start_date: mode === HISTORICAL_SCENARIO ? start?.format(DATE_FMT) : undefined,
              end_date: mode === HISTORICAL_SCENARIO ? end?.format(DATE_FMT) : undefined,
            }),
          );
          hide();
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input placeholder="Blueprint name" />
        </Form.Item>
        <Form.Item label="Session type" name="scenario_type">
          <Select options={[
            { label: 'Historical', value: HISTORICAL_SCENARIO },
            { label: 'Real time', value: REAL_TIME_SCENARIO },
          ]} />
        </Form.Item>
        {mode === HISTORICAL_SCENARIO && <>
          <Form.Item label="Execution engine" name="execution_engine"><Select options={[
            { label: 'Legacy', value: 'legacy' }, { label: 'Fast Classic cash', value: 'fast' },
          ]} /></Form.Item>
          {engine === 'fast' && <Form.Item label="Trainer advancement" name="fast_advancement_mode"><Select options={[
            { label: 'Every weekday', value: 'daily' }, { label: 'News dates with daily playback', value: 'news' },
          ]} /></Form.Item>}
        </>}
        {mode === HISTORICAL_SCENARIO && <Form.Item
          label="Historical period"
          name="dates"
          rules={[{ required: true, message: 'A start and end date are required' }]}
        >
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            // Weekends are rejected by the backend (and would break historical resume).
            disabledDate={(d: Dayjs) => d.day() === 0 || d.day() === 6}
          />
        </Form.Item>}
        <Form.Item label="Reporting currency" name="reporting_currency">
          <Select
            options={REPORTING_CURRENCIES.map((c: string) => ({
              label: c,
              value: c,
            }))}
          />
        </Form.Item>
        <Form.Item label="Category" name="category">
          <Input placeholder="Optional category" />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea placeholder="Optional description" rows={3} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={hide} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={waiting}>
            Create
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateBlueprintModal;
